import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import {
  Button,
  Text,
  Note,
  InlineSpinner,
  HStack,
} from '@freee_jp/vibes';
import {
  MdSync as MdSyncIcon,
  MdBuild as MdBuildIcon,
  MdMessage as MdMessageIcon,
  MdCheckCircle as MdCheckCircleIcon,
  MdError as MdErrorIcon,
  MdWarning as MdWarningIcon,
  MdAutoAwesome as MdAutoAwesomeIcon,
  MdInfo as MdInfoIcon,
  MdRadioButtonUnchecked as MdDotIcon,
} from 'react-icons/md';
import { useEventPolling, TimelineItem } from '../hooks/useEventPolling';
import { sendUserMessage } from '../api/client';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ActivityPanelProps {
  open: boolean;
  onClose: () => void;
  /** Override polling interval, in ms. Defaults to 4000. */
  intervalMs?: number;
}

// ---------------------------------------------------------------------------
// Visual mapping (by TimelineItem.kind)
// ---------------------------------------------------------------------------

interface IconDef {
  icon: React.ReactNode;
  bg: string;
  color: string;
  label: string;
}

const KIND_ICONS: Record<string, IconDef> = {
  progress: { icon: <MdSyncIcon size={14} />, bg: '#e8f0fe', color: '#2e7cf6', label: 'progress' },
  tool: { icon: <MdBuildIcon size={14} />, bg: '#f5f3f2', color: '#6b6560', label: 'tool' },
  message: { icon: <MdMessageIcon size={14} />, bg: '#e8f0fe', color: '#2e7cf6', label: 'message' },
  result: { icon: <MdCheckCircleIcon size={14} />, bg: '#e6f4ea', color: '#338650', label: 'result' },
  error: { icon: <MdErrorIcon size={14} />, bg: '#fdecec', color: '#c33939', label: 'error' },
  warning: { icon: <MdWarningIcon size={14} />, bg: '#fff4e5', color: '#c67320', label: 'warning' },
  success: { icon: <MdAutoAwesomeIcon size={14} />, bg: '#e6f4ea', color: '#338650', label: 'success' },
  info: { icon: <MdInfoIcon size={14} />, bg: '#f5f3f2', color: '#6b6560', label: 'info' },
};

const DEFAULT_ICON: IconDef = {
  icon: <MdDotIcon size={10} />,
  bg: '#f5f3f2',
  color: '#6b6560',
  label: 'event',
};

function iconFor(item: TimelineItem): IconDef {
  const actor = (item.actor || '').toLowerCase();
  if (item.kind === 'message') {
    if (actor === 'user') {
      return { icon: <MdMessageIcon size={14} />, bg: '#edebea', color: '#252525', label: 'message' };
    }
    return KIND_ICONS.message;
  }
  return KIND_ICONS[item.kind] || DEFAULT_ICON;
}

function actorLabel(a: string): string {
  const actor = (a || '').toLowerCase();
  if (actor === 'claude' || actor === 'claude_code') return 'Claude';
  if (actor === 'user') return 'ユーザー';
  if (actor === 'tool') return 'Tool';
  return a || '—';
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const DRAWER_WIDTH = '420px';

const S = {
  // Fixed-position drawer
  drawer: (open: boolean): React.CSSProperties => ({
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: DRAWER_WIDTH,
    maxWidth: '95vw',
    background: '#fff',
    borderLeft: '1px solid #dbd9d7',
    boxShadow: open ? '-8px 0 24px rgba(0,0,0,0.08)' : 'none',
    transform: open ? 'translateX(0)' : 'translateX(100%)',
    transition: 'transform 0.25s ease',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1500,
  }),
  backdrop: (open: boolean): React.CSSProperties => ({
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(37, 37, 37, 0.08)',
    pointerEvents: open ? 'auto' : 'none',
    opacity: open ? 1 : 0,
    transition: 'opacity 0.2s ease',
    zIndex: 1499,
  }),
  header: {
    padding: '14px 18px',
    borderBottom: '1px solid #edebea',
    background: '#fafaf8',
    flexShrink: 0,
  } as React.CSSProperties,
  body: {
    flex: 1,
    overflowY: 'auto' as const,
    padding: '12px 16px 16px',
    background: '#f7f5f5',
  } as React.CSSProperties,
  footer: {
    padding: '10px 16px',
    borderTop: '1px solid #edebea',
    background: '#fff',
    fontSize: '0.6875rem',
    color: '#8a8580',
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  } as React.CSSProperties,
  item: {
    display: 'flex',
    gap: '10px',
    padding: '8px 0',
    alignItems: 'flex-start',
  } as React.CSSProperties,
  iconCircle: (bg: string, color: string) => ({
    flexShrink: 0,
    width: '28px',
    height: '28px',
    borderRadius: '14px',
    background: bg,
    color,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.8125rem',
    fontWeight: 700,
    border: `1px solid ${color}22`,
    lineHeight: 1,
  }),
  bubble: {
    flex: 1,
    minWidth: 0,
    background: '#fff',
    border: '1px solid #edebea',
    borderRadius: '8px',
    padding: '8px 10px',
  } as React.CSSProperties,
  bubbleHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: '2px',
    gap: '6px',
  } as React.CSSProperties,
  actorLabel: {
    fontSize: '0.6875rem',
    fontWeight: 700,
    color: '#252525',
    letterSpacing: '0.01em',
  } as React.CSSProperties,
  titleLabel: {
    fontSize: '0.625rem',
    fontWeight: 600,
    color: '#6b6560',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
    marginLeft: '6px',
  } as React.CSSProperties,
  ts: {
    fontSize: '0.625rem',
    color: '#8a8580',
    fontVariantNumeric: 'tabular-nums',
    marginLeft: 'auto',
    whiteSpace: 'nowrap' as const,
  } as React.CSSProperties,
  content: {
    fontSize: '0.8125rem',
    color: '#252525',
    lineHeight: 1.5,
    wordBreak: 'break-word' as const,
    whiteSpace: 'pre-wrap' as const,
  } as React.CSSProperties,
  meta: {
    marginTop: '4px',
    fontSize: '0.625rem',
    color: '#8a8580',
    fontVariantNumeric: 'tabular-nums' as const,
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '10px',
  } as React.CSSProperties,
  progressBar: (pct: number): React.CSSProperties => ({
    marginTop: '6px',
    height: '4px',
    width: '100%',
    background: '#edebea',
    borderRadius: '2px',
    overflow: 'hidden',
    position: 'relative',
  }),
  progressFill: (pct: number, color: string): React.CSSProperties => ({
    height: '100%',
    width: `${Math.min(100, Math.max(0, pct))}%`,
    background: color,
    transition: 'width 0.25s ease',
  }),
  emptyBox: {
    textAlign: 'center' as const,
    padding: '48px 16px',
    color: '#8a8580',
    fontSize: '0.8125rem',
  } as React.CSSProperties,
  errorBox: {
    margin: '8px 0',
    padding: '8px 10px',
    border: '1px solid #c33939',
    background: '#fdecec',
    color: '#c33939',
    fontSize: '0.75rem',
    borderRadius: '6px',
  } as React.CSSProperties,
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseDate(iso: string): Date | null {
  if (!iso) return null;
  // API sometimes returns "YYYY-MM-DD HH:MM:SS" (no T, no Z). Convert.
  let s = iso;
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}/.test(s)) {
    s = s.replace(' ', 'T') + 'Z';
  }
  const d = new Date(s);
  if (isNaN(d.getTime())) return null;
  return d;
}

function fmtTime(iso: string): string {
  const d = parseDate(iso);
  if (!d) return iso;
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const ss = String(d.getSeconds()).padStart(2, '0');
  return `${hh}:${mm}:${ss}`;
}

function fmtDate(iso: string): string {
  const d = parseDate(iso);
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function shortId(id?: string | null, max: number = 8): string {
  if (!id) return '';
  return id.length > max ? `${id.slice(0, max)}…` : id;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ActivityPanel: React.FC<ActivityPanelProps> = ({
  open,
  onClose,
  intervalMs = 4000,
}) => {
  const { items, isPolling, error, refresh, latestSeq } = useEventPolling({
    enabled: open,
    intervalMs,
  });

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [autoScroll, setAutoScroll] = useState<boolean>(true);
  const lastCountRef = useRef<number>(0);

  // Group items by date for a light-weight day separator
  const grouped = useMemo(() => {
    const acc: { date: string; items: TimelineItem[] }[] = [];
    items.forEach((it) => {
      const d = fmtDate(it.timestamp);
      const last = acc[acc.length - 1];
      if (!last || last.date !== d) {
        acc.push({ date: d, items: [it] });
      } else {
        last.items.push(it);
      }
    });
    return acc;
  }, [items]);

  // Auto-scroll to bottom when new items arrive (if user is near bottom)
  useEffect(() => {
    if (!scrollRef.current) return;
    if (items.length === lastCountRef.current) return;
    lastCountRef.current = items.length;
    if (!autoScroll) return;
    const el = scrollRef.current;
    el.scrollTop = el.scrollHeight;
  }, [items, autoScroll]);

  // When the panel opens (or reopens), snap to bottom and reset autoScroll.
  // This is the default "stick to latest" behavior; user can scroll up to browse history.
  useEffect(() => {
    if (!open) return;
    setAutoScroll(true);
    // Defer one frame so layout is applied (drawer transitions in, content height settles).
    const r = requestAnimationFrame(() => {
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
    return () => cancelAnimationFrame(r);
  }, [open]);

  // Track if user scrolled away from bottom
  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
    setAutoScroll(nearBottom);
  }, []);

  const jumpToLatest = useCallback(() => {
    setAutoScroll(true);
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, []);

  // Don't render drawer DOM when closed to avoid background network cost.
  // The backdrop always gets rendered to preserve transition easing, but the
  // polling hook is controlled via the `enabled` flag above.
  return (
    <>
      {/* Backdrop */}
      <div
        style={S.backdrop(open)}
        onClick={open ? onClose : undefined}
        aria-hidden={!open}
      />

      {/* Drawer */}
      <div style={S.drawer(open)} role="complementary" aria-label="Claude Activity" aria-hidden={!open}>
        <div style={S.header}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <Text weight="bold" size={1}>
                Claude Activity
              </Text>
              <div style={{ marginTop: '2px' }}>
                <Note>
                  Hub (/api/timeline) をリアルタイム購読中
                </Note>
              </div>
            </div>
            <HStack gap={0.25}>
              <Button small appearance="tertiary" onClick={refresh}>
                更新
              </Button>
              <Button small appearance="tertiary" onClick={onClose}>
                閉じる
              </Button>
            </HStack>
          </div>
          <div style={{ marginTop: '8px', display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{
              fontSize: '0.625rem',
              color: '#8a8580',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
            }}>
              {isPolling ? <InlineSpinner isLoading /> : <span style={{
                width: 6, height: 6, borderRadius: '50%', background: '#338650', display: 'inline-block',
              }} />}
              <span>{isPolling ? '取得中…' : `待機中 (${intervalMs / 1000}s)`}</span>
            </span>
            <span style={{ fontSize: '0.625rem', color: '#8a8580' }}>
              seq: {latestSeq}
            </span>
            <span style={{ fontSize: '0.625rem', color: '#8a8580' }}>
              items: {items.length}
            </span>
          </div>
        </div>

        <div style={{ position: 'relative', flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div ref={scrollRef} style={S.body} onScroll={handleScroll}>
          {error && <div style={S.errorBox}>エラー: {error}</div>}

          {items.length === 0 && !isPolling && !error && (
            <div style={S.emptyBox}>
              <div style={{ fontSize: '1.75rem', marginBottom: '10px' }}>~</div>
              <div>まだアクティビティはありません</div>
              <div style={{ marginTop: '6px', fontSize: '0.6875rem' }}>
                Claude/Workerからイベントが届くとここに表示されます
              </div>
            </div>
          )}

          {grouped.map((group) => (
            <div key={group.date || 'no-date'} style={{ marginBottom: '4px' }}>
              {group.date && (
                <div
                  style={{
                    textAlign: 'center',
                    margin: '10px 0 6px',
                    fontSize: '0.625rem',
                    color: '#8a8580',
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                  }}
                >
                  — {group.date} —
                </div>
              )}
              {group.items.map((it) => {
                const ic = iconFor(it);
                const hasProgress = it.progress && typeof it.progress.total === 'number' && it.progress.total > 0;
                const pct = hasProgress
                  ? Math.round(((it.progress!.current || 0) / it.progress!.total) * 100)
                  : 0;
                // Show title only if it adds information beyond the kind label.
                const showTitle = !!it.title && it.title.toLowerCase() !== it.kind.toLowerCase();
                return (
                  <div key={it.id} style={S.item}>
                    <div style={S.iconCircle(ic.bg, ic.color)} title={ic.label}>
                      {ic.icon}
                    </div>
                    <div style={S.bubble}>
                      <div style={S.bubbleHead}>
                        <span style={S.actorLabel}>{actorLabel(it.actor)}</span>
                        {showTitle && (
                          <span style={{ ...S.titleLabel, color: ic.color }}>
                            {it.title}
                            {it.status && (
                              <span style={{ color: '#8a8580', marginLeft: '6px' }}>
                                · {it.status}
                              </span>
                            )}
                          </span>
                        )}
                        <span style={S.ts}>{fmtTime(it.timestamp)}</span>
                      </div>
                      {it.content && <div style={S.content}>{it.content}</div>}
                      {hasProgress && (
                        <>
                          <div style={S.progressBar(pct)}>
                            <div style={S.progressFill(pct, ic.color)} />
                          </div>
                          <div style={{ ...S.meta, marginTop: '3px' }}>
                            <span>
                              {it.progress!.current} / {it.progress!.total} ({pct}%)
                            </span>
                          </div>
                        </>
                      )}
                      {(it.run_id || it.entry_id || it.session_id || it.instruction_id) && (
                        <div style={S.meta}>
                          <span>#{it.sequence}</span>
                          {it.run_id && <span>run:{shortId(it.run_id)}</span>}
                          {it.entry_id && <span>entry:{shortId(it.entry_id)}</span>}
                          {it.session_id && <span>sess:{shortId(it.session_id)}</span>}
                          {it.instruction_id && <span>instr:{shortId(it.instruction_id)}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {/* Floating "jump to latest" button, bottom-center, shown when user scrolled up */}
        {!autoScroll && items.length > 0 && (
          <button
            onClick={jumpToLatest}
            style={{
              position: 'absolute',
              left: '50%',
              bottom: '12px',
              transform: 'translateX(-50%)',
              background: '#2e7cf6',
              color: '#fff',
              border: 'none',
              borderRadius: '20px',
              padding: '8px 16px',
              fontSize: '0.75rem',
              fontWeight: 600,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(46, 124, 246, 0.35)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              zIndex: 10,
              transition: 'transform 0.15s, box-shadow 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateX(-50%) translateY(-1px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(46, 124, 246, 0.5)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateX(-50%)';
              e.currentTarget.style.boxShadow = '0 2px 8px rgba(46, 124, 246, 0.35)';
            }}
            aria-label="最新メッセージへジャンプ"
          >
            <span>↓</span>
            <span>最新メッセージへ</span>
          </button>
        )}
        </div>

        <ChatInput />

        <div style={S.footer}>
          <span>Hub: invoice-review.system-0f7.workers.dev</span>
          <span>{autoScroll ? 'auto-scroll' : '固定表示'}</span>
        </div>
      </div>
    </>
  );
};

// ---------------------------------------------------------------------------
// Chat input — send user messages to Claude via /api/instructions
// ---------------------------------------------------------------------------

const ChatInput: React.FC = () => {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const handleSend = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed || sending) return;
    setSending(true);
    try {
      await sendUserMessage(trimmed);
      setText('');
      setToast('送信しました');
      setTimeout(() => setToast(null), 2500);
    } catch (e: any) {
      setToast(`送信失敗: ${e?.message || 'unknown'}`);
      setTimeout(() => setToast(null), 4000);
    } finally {
      setSending(false);
    }
  }, [text, sending]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      handleSend();
    }
  }, [handleSend]);

  return (
    <div style={{
      borderTop: '1px solid #e9e7e3',
      background: '#fafaf8',
      padding: '10px 14px 12px',
      position: 'relative',
    }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Claudeへ質問・指示を入力... (Enterで送信, Shift+Enterで改行)"
          disabled={sending}
          rows={2}
          style={{
            flex: 1,
            resize: 'none',
            border: '1px solid #d6d3ce',
            borderRadius: 6,
            padding: '8px 10px',
            font: 'inherit',
            fontSize: 13,
            lineHeight: 1.45,
            background: '#fff',
            outline: 'none',
            minHeight: 44,
            maxHeight: 160,
          }}
        />
        <Button
          appearance="primary"
          small
          onClick={handleSend}
          disabled={!text.trim() || sending}
        >
          {sending ? '送信中' : '送信'}
        </Button>
      </div>
      {toast && (
        <div style={{
          position: 'absolute',
          top: -30,
          right: 14,
          background: '#252525',
          color: '#fff',
          fontSize: 12,
          padding: '4px 10px',
          borderRadius: 4,
          boxShadow: '0 2px 8px rgba(0,0,0,.2)',
        }}>
          {toast}
        </div>
      )}
    </div>
  );
};

export default ActivityPanel;
